import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabase'
import { useAuth } from '../context/AuthContext'
import { useNavigate } from 'react-router-dom'
import Topbar from '../components/Topbar'
import { useToast } from '../context/ToastContext'
import { useTranslation } from '../i18n/LanguageContext'

// Design system — all colors via CSS vars directly in JSX

const MONTHS       = ['January','February','March','April','May','June','July','August','September','October','November','December']
const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
const inp = { width: '100%', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '9px 12px', fontSize: '12.5px', outline: 'none', background: 'var(--bg-card)', color: 'var(--text-primary)', fontFamily: 'var(--font-ui)', boxSizing: 'border-box', transition: 'border-color 0.15s' }

function getStatus(actual, goal, t) {
  if (!goal || goal === 0) return null
  const pct = (actual / goal) * 100
  if (pct >= 100) return { pct, color: 'var(--success)', bg: '#F0FDF4', border: '#BBF7D0', icon: 'check',   label: t('goals.status_met') }
  if (pct >= 80)  return { pct, color: 'var(--warning)', bg: '#FFF8E6', border: '#FDE68A', icon: 'almost',  label: t('goals.status_almost') }
  if (pct >= 50)  return { pct, color: '#EA580C',        bg: '#FFF7ED', border: '#FED7AA', icon: 'effort',  label: t('goals.status_effort') }
  return             { pct, color: 'var(--error)',   bg: '#FEF2F2', border: '#FECACA', icon: 'behind',  label: t('goals.status_behind') }
}

function getMotivation(pct, isCurrent, t) {
  if (pct === null || pct === undefined) return null
  if (pct >= 120) return isCurrent ? t('goals.motivation_smashed_current') : t('goals.motivation_smashed_past').replace('{n}', Math.round(pct - 100))
  if (pct >= 100) return isCurrent ? t('goals.motivation_met_current') : t('goals.motivation_met_past')
  if (pct >= 80)  return isCurrent ? t('goals.motivation_close_current') : t('goals.motivation_close_past')
  if (pct >= 50)  return isCurrent ? t('goals.motivation_half_current') : t('goals.motivation_half_past')
  if (pct >= 1)   return isCurrent ? t('goals.motivation_low_current') : t('goals.motivation_low_past')
  return isCurrent ? t('goals.motivation_zero_current') : t('goals.motivation_zero_past')
}

function CircleProgress({ pct, size = 80, stroke = 7, color = 'var(--primary)' }) {
  const r    = (size - stroke) / 2
  const circ = 2 * Math.PI * r
  const filled = Math.min(100, pct || 0)
  const offset = circ - (filled / 100) * circ
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="var(--border)" strokeWidth={stroke} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
        transform={`rotate(-90 ${size/2} ${size/2})`} />
    </svg>
  )
}

function HeatCell({ pct, label, onClick, isCurrent }) {
  const bg = pct === null ? 'var(--border)' : pct >= 100 ? '#16A34A' : pct >= 80 ? '#4ADE80' : pct >= 50 ? '#FCD34D' : pct >= 20 ? '#F97316' : pct > 0 ? '#EF4444' : 'var(--bg-muted)'
  return (
    <div onClick={onClick}
      title={label + (pct !== null ? ' — ' + Math.round(pct) + '%' : '')}
      style={{ aspectRatio: '1', borderRadius: 'var(--radius-sm)', background: bg, cursor: 'pointer', border: isCurrent ? '2px solid var(--primary)' : '2px solid transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: 700, color: pct !== null && pct >= 50 ? '#fff' : 'var(--text-primary)', transition: 'transform 0.1s' }}
      onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
      onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}>
      {label}
    </div>
  )
}

function StatusIcon({ icon, size = 13, color = 'currentColor' }) {
  const icons = {
    check:    <polyline points="20 6 9 17 4 12"/>,
    almost:   <><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></>,
    effort:   <><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></>,
    behind:   <><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></>,
    flame:    <path d="M8.5 14.5A2.5 2.5 0 0011 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 01-7 7 7 7 0 01-7-7c0-1.153.433-2.294 1-3a2.5 2.5 0 002.5 2.5z"/>,
    trophy:   <><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"/></>,
    target:   <><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></>,
    download: <><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></>,
  }
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display:'inline-block', verticalAlign:'middle', flexShrink:0 }}>{icons[icon]}</svg>
}

function Goals() {
  const { seller, isFeatureAllowed } = useAuth()
  const navigate     = useNavigate()
  const { showToast } = useToast()
  const { t }        = useTranslation()
  const currentYear  = new Date().getFullYear()
  const currentMonth = new Date().getMonth() + 1
  const today        = new Date()
  const daysInMonth  = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate()
  const dayOfMonth   = today.getDate()

  const sym = useCallback(() => seller?.seller_mode === 'international' ? '$' : '৳', [seller])
  const fmt = useCallback((n) => {
    const s = sym(); const abs = Math.abs(Math.round(n || 0))
    if (abs >= 100000) return s + Math.round(abs / 1000) + 'k'
    return s + abs.toLocaleString()
  }, [sym])

  const [selectedYear, setSelectedYear]      = useState(currentYear)
  const [goals, setGoals]                    = useState({})
  const [actuals, setActuals]                = useState({})
  const [lastYearActuals, setLastYearActuals] = useState({})
  const [notes, setNotes]                    = useState({})
  const [annualGoalRev, setAnnualGoalRev]    = useState('')
  const [loading, setLoading]                = useState(true)
  const [saving, setSaving]                  = useState(false)
  const [expandedMonth, setExpandedMonth]    = useState(currentMonth)
  const [activeTab, setActiveTab]            = useState('overview')

  useEffect(() => {
    if (seller?.id) fetchAll()
  }, [seller, selectedYear]) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => {
    setLoading(true)
    const { data: goalsData } = await supabase.from('seller_goals').select('*').eq('seller_id', seller.id).eq('year', selectedYear)
    const goalsMap = {}; const notesMap = {}; let annualRev = ''
    goalsData?.forEach(g => {
      if (g.month === 0) { annualRev = g.revenue_goal || '' }
      else if (g.month !== null) {
        goalsMap[g.month] = { revenue_goal: g.revenue_goal || '', order_goal: g.order_goal || '' }
        if (g.notes) notesMap[g.month] = g.notes
      }
    })
    setGoals(goalsMap); setNotes(notesMap); setAnnualGoalRev(annualRev)

    const fetchActualsForYear = async (year) => {
      const { data } = await supabase.from('orders').select('total_amount, created_at, status')
        .eq('seller_id', seller.id)
        .gte('created_at', new Date(year, 0, 1).toISOString())
        .lte('created_at', new Date(year, 11, 31, 23, 59, 59).toISOString())
      const map = {}
      data?.forEach(o => {
        const m = new Date(o.created_at).getMonth() + 1
        if (!map[m]) map[m] = { revenue: 0, orders: 0 }
        map[m].orders++
        if (o.status !== 'return') map[m].revenue += Number(o.total_amount || 0)
      })
      return map
    }
    const [curr, last] = await Promise.all([fetchActualsForYear(selectedYear), fetchActualsForYear(selectedYear - 1)])
    setActuals(curr); setLastYearActuals(last); setLoading(false)
  }

  const handleSave = async () => {
    setSaving(true)
    await supabase.from('seller_goals').upsert({ seller_id: seller.id, year: selectedYear, month: 0, revenue_goal: Number(annualGoalRev) || 0, order_goal: 0, notes: null }, { onConflict: 'seller_id,year,month' })
    for (let m = 1; m <= 12; m++) {
      const g = goals[m] || {}
      await supabase.from('seller_goals').upsert({ seller_id: seller.id, year: selectedYear, month: m, revenue_goal: Number(g.revenue_goal) || 0, order_goal: Number(g.order_goal) || 0, notes: notes[m] || null }, { onConflict: 'seller_id,year,month' })
    }
    await fetchAll(); setSaving(false)
    showToast(t('goals.toast_saved'))
  }

  const setGoalFromLastYear = (m, pct) => {
    const last = lastYearActuals[m] || { revenue: 0, orders: 0 }
    setGoals(g => ({ ...g, [m]: { revenue_goal: Math.round(last.revenue * (1 + pct / 100)) || '', order_goal: Math.round(last.orders * (1 + pct / 100)) || '' } }))
  }

  const distributeAnnualGoal = () => {
    if (!annualGoalRev) return
    const monthly = Math.round(Number(annualGoalRev) / 12)
    const newGoals = {}
    for (let m = 1; m <= 12; m++) newGoals[m] = { ...goals[m], revenue_goal: monthly }
    setGoals(newGoals)
    showToast(t('goals.toast_distributed'))
  }

  const yearlyGoalRev  = Object.values(goals).reduce((s, g) => s + (Number(g.revenue_goal) || 0), 0)
  const yearlyGoalOrd  = Object.values(goals).reduce((s, g) => s + (Number(g.order_goal) || 0), 0)
  const yearlyActRev   = Object.values(actuals).reduce((s, m) => s + m.revenue, 0)
  const yearlyActOrd   = Object.values(actuals).reduce((s, m) => s + m.orders, 0)
  const lastYearTotRev = Object.values(lastYearActuals).reduce((s, m) => s + m.revenue, 0)
  const lastYearTotOrd = Object.values(lastYearActuals).reduce((s, m) => s + m.orders, 0)
  const yearlyRevPct   = yearlyGoalRev > 0 ? (yearlyActRev / yearlyGoalRev) * 100 : 0
  const yearlyOrdPct   = yearlyGoalOrd > 0 ? (yearlyActOrd / yearlyGoalOrd) * 100 : 0
  const yoyRevGrowth   = lastYearTotRev > 0 ? ((yearlyActRev - lastYearTotRev) / lastYearTotRev) * 100 : null
  const yoyOrdGrowth   = lastYearTotOrd > 0 ? ((yearlyActOrd - lastYearTotOrd) / lastYearTotOrd) * 100 : null

  const streak = (() => {
    let count = 0
    for (let m = currentMonth; m >= 1; m--) {
      const g = Number(goals[m]?.revenue_goal) || 0
      const a = actuals[m]?.revenue || 0
      if (g > 0 && a >= g) count++; else break
    }
    return count
  })()

  const monthsWithGoal = Array.from({ length: currentMonth }, (_, i) => i + 1).filter(m => Number(goals[m]?.revenue_goal) > 0 && (selectedYear < currentYear || m <= currentMonth))
  const monthsHit      = monthsWithGoal.filter(m => (actuals[m]?.revenue || 0) >= Number(goals[m]?.revenue_goal))
  const achieveRate    = monthsWithGoal.length > 0 ? Math.round((monthsHit.length / monthsWithGoal.length) * 100) : null

  const bestMonth         = Object.entries(actuals).sort((a, b) => b[1].revenue - a[1].revenue)[0]
  const bestMonthLastYear = Object.entries(lastYearActuals).sort((a, b) => b[1].revenue - a[1].revenue)[0]

  const cmActual    = actuals[currentMonth]  || { revenue: 0, orders: 0 }
  const cmGoal      = goals[currentMonth]    || { revenue_goal: 0, order_goal: 0 }
  const cmRevStatus = getStatus(cmActual.revenue, Number(cmGoal.revenue_goal), t)

  const dailyAvg     = dayOfMonth > 0 ? cmActual.revenue / dayOfMonth : 0
  const projectedEOM = dailyAvg * daysInMonth
  const daysLeft     = daysInMonth - dayOfMonth
  const gapToGoal    = Number(cmGoal.revenue_goal) - cmActual.revenue

  const quarters = [
    { label: 'Q1', months: [1,2,3] }, { label: 'Q2', months: [4,5,6] },
    { label: 'Q3', months: [7,8,9] }, { label: 'Q4', months: [10,11,12] },
  ].map(q => ({ ...q, actual: q.months.reduce((s, m) => s + (actuals[m]?.revenue || 0), 0), goal: q.months.reduce((s, m) => s + (Number(goals[m]?.revenue_goal) || 0), 0), orders: q.months.reduce((s, m) => s + (actuals[m]?.orders || 0), 0) }))

  if (!isFeatureAllowed('goals')) {
    return (
      <div style={{ flex: 1, background: 'var(--bg-muted)', fontFamily: 'var(--font-ui)' }}>
        <Topbar title={t('goals.title')} />
        <div style={{ padding: '48px 24px', maxWidth: '480px', margin: '0 auto', textAlign: 'center' }}>
          <div style={{ background: 'var(--bg-card)', borderRadius: '16px', padding: '40px', border: '1px solid var(--border)' }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 12px', display: 'block' }}><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>
            <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '8px' }}>{t('goals.upgrade_title')}</div>
            <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '24px', lineHeight: 1.6 }}>{t('goals.upgrade_desc')}</div>
            <button onClick={() => navigate('/pricing')}
              style={{ width: '100%', padding: '13px', borderRadius: 'var(--radius-lg)', background: 'var(--primary)', border: 'none', fontSize: '14px', fontWeight: 700, color: '#fff', cursor: 'pointer' }}>
              {t('goals.upgrade_btn')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // SVG status icon — replaces emojis for consistency with design system
  const exportCSV = () => {
    const rows = [['Month','Revenue Goal','Revenue Actual','Revenue %','Order Goal','Order Actual','Order %']]
    for (let m = 1; m <= 12; m++) {
      const g = goals[m] || {}; const a = actuals[m] || { revenue: 0, orders: 0 }
      const rg = Number(g.revenue_goal)||0; const og = Number(g.order_goal)||0
      rows.push([MONTHS[m-1], rg, Math.round(a.revenue), rg>0?Math.round((a.revenue/rg)*100)+'%':'—', og, a.orders, og>0?Math.round((a.orders/og)*100)+'%':'—'])
    }
    const csv = rows.map(r => r.map(v => '"'+String(v).replace(/"/g,'""')+'"').join(',')).join('\n')
    const a2 = document.createElement('a'); a2.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv)
    a2.download = 'goals-' + selectedYear + '.csv'; a2.click()
  }

  return (
    <div style={{ flex: 1, overflowX: 'hidden', background: 'var(--bg-page)', fontFamily: 'var(--font-ui)' }}>
      <Topbar title={t('goals.title')} />
      <div style={{ padding: '18px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
          <div>
            <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text-primary)' }}>{t('goals.title')}</div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>{t('goals.subtitle')}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            {/* Streak badge */}
            {streak > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: 'var(--radius-full)', background: '#FFF7ED', border: '1px solid #FED7AA' }}>
                <StatusIcon icon="flame" size={13} color="#EA580C" />
                <span style={{ fontSize: '12px', fontWeight: 700, color: '#EA580C' }}>{streak} {t('goals.streak')}</span>
              </div>
            )}
            {/* Success rate badge */}
            {achieveRate !== null && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: 'var(--radius-full)', background: 'var(--primary-light)', border: '1px solid var(--primary-border)' }}>
                <StatusIcon icon="target" size={13} color="var(--primary)" />
                <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--primary)' }}>{achieveRate}% {t('goals.success_rate')}</span>
              </div>
            )}
            {/* Year picker */}
            <div style={{ display: 'flex', gap: '4px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-full)', padding: '3px' }}>
              {[currentYear - 1, currentYear, currentYear + 1].map(y => (
                <button key={y} onClick={() => setSelectedYear(y)}
                  style={{ padding: '5px 14px', borderRadius: 'var(--radius-full)', border: 'none', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', background: selectedYear === y ? 'var(--primary)' : 'transparent', color: selectedYear === y ? '#fff' : 'var(--text-muted)', transition: 'all 0.12s' }}>
                  {y}
                </button>
              ))}
            </div>
            {/* Export */}
            <button onClick={exportCSV}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'var(--bg-card)', fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)', cursor: 'pointer' }}>
              <StatusIcon icon="download" size={13} color="var(--text-muted)" /> Export
            </button>
          </div>
        </div>

        {/* ── TABS ── */}
        <div style={{ display: 'flex', gap: '4px', padding: '4px', borderRadius: 'var(--radius-lg)', background: 'var(--bg-card)', border: '1px solid var(--border)', width: 'fit-content', marginBottom: '16px' }}>
          {[
            { key: 'overview',  label: t('goals.tab_overview'),  ic: 'target' },
            { key: 'monthly',   label: t('goals.tab_monthly'),   ic: 'check' },
            { key: 'quarterly', label: t('goals.tab_quarterly'), ic: 'trophy' },
          ].map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 14px', borderRadius: 'var(--radius-md)', border: 'none', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', background: activeTab === tab.key ? 'var(--primary)' : 'transparent', color: activeTab === tab.key ? '#fff' : 'var(--text-muted)', transition: 'all 0.12s', whiteSpace: 'nowrap' }}>
              <StatusIcon icon={tab.ic} size={13} color={activeTab === tab.key ? '#fff' : 'var(--text-muted)'} />
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', padding: '48px', textAlign: 'center' }}>
            <div style={{ width: '24px', height: '24px', borderRadius: '50%', border: '2.5px solid var(--primary)', borderTopColor: 'transparent', animation: 'goal-spin 0.7s linear infinite', margin: '0 auto 12px' }} />
            <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{t('goals.loading')}</div>
          </div>
        ) : (
          <>

          {/* ══════════════════════════════════════════════
              OVERVIEW TAB
          ══════════════════════════════════════════════ */}
          {activeTab === 'overview' && (
            <div className="pc-fade-up">

              {/* ── Annual goal — PROMOTED to top ── */}
              <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--primary-border)', padding: '16px', marginBottom: '14px', boxShadow: 'var(--shadow-sm)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <StatusIcon icon="target" size={15} color="var(--primary)" />
                  <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>{t('goals.annual_title')}</div>
                </div>
                <div style={{ fontSize: '11.5px', color: 'var(--text-muted)', marginBottom: '12px' }}>{t('goals.annual_sub').replace('{year}', selectedYear)}</div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <input type="number" value={annualGoalRev} onChange={e => setAnnualGoalRev(e.target.value)}
                    placeholder={'e.g. 1000000 — annual target for ' + selectedYear}
                    style={{ ...inp, flex: 1, minWidth: '200px' }}
                    onFocus={e => e.target.style.borderColor = 'var(--primary)'}
                    onBlur={e => e.target.style.borderColor = 'var(--border)'} />
                  <button onClick={distributeAnnualGoal} disabled={!annualGoalRev}
                    style={{ padding: '9px 16px', borderRadius: 'var(--radius-md)', background: annualGoalRev ? 'var(--primary)' : 'var(--border)', border: 'none', fontSize: '12.5px', fontWeight: 600, color: '#fff', cursor: annualGoalRev ? 'pointer' : 'not-allowed', flexShrink: 0 }}>
                    {t('goals.annual_distribute')}
                  </button>
                </div>
                {annualGoalRev && (
                  <div style={{ fontSize: '11.5px', color: 'var(--text-muted)', marginTop: '8px' }}>
                    {fmt(Number(annualGoalRev))} {t('goals.annual_target')} · {fmt(Math.round(Number(annualGoalRev) / 12))} {t('goals.annual_per_month')}
                    {yearlyActRev > 0 && <span> · <strong style={{ color: yearlyActRev >= Number(annualGoalRev) ? 'var(--success)' : 'var(--primary)' }}>{Math.round((yearlyActRev / Number(annualGoalRev)) * 100)}%</strong> {t('goals.annual_achieved')}</span>}
                  </div>
                )}
              </div>

              {/* ── 2-col: Current month hero + Year overview ── */}
              <div className="goal-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>

                {/* Current month hero */}
                {selectedYear === currentYear && (
                  <div className="pc-card-hover" style={{ background: 'var(--primary)', borderRadius: 'var(--radius-lg)', padding: '20px', color: '#fff', boxShadow: 'var(--shadow-sm)' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '16px' }}>
                      <div>
                        <div style={{ fontSize: '11px', fontWeight: 700, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '4px' }}>{t('goals.this_month')}</div>
                        <div style={{ fontSize: '20px', fontWeight: 700, color: '#fff' }}>{MONTHS[currentMonth - 1]}</div>
                        {cmRevStatus && (
                          <div style={{ marginTop: '6px', display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '11.5px', fontWeight: 600, padding: '3px 10px', borderRadius: 'var(--radius-full)', background: 'rgba(255,255,255,0.12)' }}>
                            <StatusIcon icon={cmRevStatus.icon} size={11} color="#fff" />
                            {cmRevStatus.label}
                          </div>
                        )}
                      </div>
                      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <CircleProgress pct={cmRevStatus?.pct || 0} size={84} stroke={7} color={cmRevStatus?.color || 'rgba(255,255,255,0.6)'} />
                        <div style={{ position: 'absolute', textAlign: 'center' }}>
                          <div style={{ fontSize: '14px', fontWeight: 700, color: '#fff' }}>{cmRevStatus ? Math.round(cmRevStatus.pct) + '%' : '—'}</div>
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '14px' }}>
                      {[
                        { label: t('goals.revenue'), value: fmt(cmActual.revenue), sub: Number(cmGoal.revenue_goal) > 0 ? t('goals.of') + ' ' + fmt(Number(cmGoal.revenue_goal)) : t('goals.no_goal_set') },
                        { label: t('goals.orders'),  value: cmActual.orders,       sub: Number(cmGoal.order_goal) > 0 ? t('goals.of') + ' ' + Number(cmGoal.order_goal) + ' ' + t('goals.of_goal') : t('goals.no_goal_set') },
                      ].map((s, i) => (
                        <div key={i} style={{ padding: '10px 12px', borderRadius: 'var(--radius-md)', background: 'rgba(255,255,255,0.08)' }}>
                          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.55)', fontWeight: 600, marginBottom: '4px' }}>{s.label}</div>
                          <div style={{ fontSize: '18px', fontWeight: 700, color: '#fff' }}>{s.value}</div>
                          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.45)', marginTop: '2px' }}>{s.sub}</div>
                        </div>
                      ))}
                    </div>

                    {Number(cmGoal.revenue_goal) > 0 && (
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'rgba(255,255,255,0.55)', marginBottom: '5px' }}>
                          <span>{t('goals.revenue_progress')}</span>
                          <span>{Math.round(Math.min(100, (cmActual.revenue / Number(cmGoal.revenue_goal)) * 100))}%</span>
                        </div>
                        <div style={{ height: '6px', borderRadius: 'var(--radius-full)', background: 'rgba(255,255,255,0.15)' }}>
                          <div style={{ height: '100%', borderRadius: 'var(--radius-full)', width: Math.min(100, (cmActual.revenue / Number(cmGoal.revenue_goal)) * 100) + '%', background: cmRevStatus?.color || 'rgba(255,255,255,0.6)', transition: 'width 0.4s' }} />
                        </div>
                        {Number(cmGoal.order_goal) > 0 && (
                          <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'rgba(255,255,255,0.55)', marginBottom: '5px', marginTop: '8px' }}>
                              <span>{t('goals.orders_progress')}</span>
                              <span>{Math.round(Math.min(100, (cmActual.orders / Number(cmGoal.order_goal)) * 100))}%</span>
                            </div>
                            <div style={{ height: '6px', borderRadius: 'var(--radius-full)', background: 'rgba(255,255,255,0.15)' }}>
                              <div style={{ height: '100%', borderRadius: 'var(--radius-full)', width: Math.min(100, (cmActual.orders / Number(cmGoal.order_goal)) * 100) + '%', background: '#EC4899', transition: 'width 0.4s' }} />
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    {/* Pace projection */}
                    {Number(cmGoal.revenue_goal) > 0 && (
                      <div style={{ padding: '10px 12px', borderRadius: 'var(--radius-md)', background: 'rgba(255,255,255,0.08)', marginBottom: '10px' }}>
                        <div style={{ fontSize: '11px', fontWeight: 700, color: 'rgba(255,255,255,0.6)', marginBottom: '4px' }}>{t('goals.pace_title')}</div>
                        <div style={{ fontSize: '12px', color: '#fff' }}>
                          {t('goals.pace_daily_avg')} <strong>{fmt(dailyAvg)}</strong> · {t('goals.pace_projected')} <strong style={{ color: projectedEOM >= Number(cmGoal.revenue_goal) ? '#4ADE80' : '#fff' }}>{fmt(projectedEOM)}</strong>
                        </div>
                        {gapToGoal > 0
                          ? <div style={{ fontSize: '11.5px', color: 'rgba(255,255,255,0.55)', marginTop: '4px' }}>{t('goals.pace_need_more')} <strong style={{ color: '#fff' }}>{fmt(gapToGoal)}</strong> in <strong style={{ color: '#fff' }}>{daysLeft}</strong> {t('goals.pace_days')}</div>
                          : <div style={{ fontSize: '11.5px', color: '#4ADE80', marginTop: '4px' }}>{t('goals.pace_reached')}</div>
                        }
                      </div>
                    )}

                    {cmRevStatus && Number(cmGoal.revenue_goal) > 0 && (
                      <div style={{ padding: '10px 12px', borderRadius: 'var(--radius-md)', background: 'rgba(255,255,255,0.08)', fontSize: '12px', color: 'rgba(255,255,255,0.8)' }}>
                        {getMotivation(cmRevStatus.pct, true, t)}
                      </div>
                    )}
                  </div>
                )}

                {/* Year overview */}
                <div className="pc-card-hover" style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', padding: '16px', boxShadow: 'var(--shadow-sm)' }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '14px' }}>{selectedYear} {t('goals.year_overview')}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '14px' }}>
                    {[
                      { label: t('goals.revenue'), pct: yearlyRevPct, actual: fmt(yearlyActRev), goal: yearlyGoalRev > 0 ? fmt(yearlyGoalRev) : null, growth: yoyRevGrowth, color: 'var(--primary)' },
                      { label: t('goals.orders'),  pct: yearlyOrdPct, actual: yearlyActOrd,      goal: yearlyGoalOrd > 0 ? yearlyGoalOrd : null,         growth: yoyOrdGrowth, color: '#EC4899' },
                    ].map((item, i) => (
                      <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '12px', borderRadius: 'var(--radius-md)', background: 'var(--bg-muted)' }}>
                        <div style={{ position: 'relative', marginBottom: '8px' }}>
                          <CircleProgress pct={item.pct} size={80} stroke={7} color={item.color} />
                          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>{Math.round(item.pct)}%</div>
                          </div>
                        </div>
                        <div style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '4px' }}>{item.label}</div>
                        <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)' }}>{item.actual}</div>
                        {item.goal && <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>of {item.goal}</div>}
                        {item.growth !== null && item.growth !== undefined && (
                          <div style={{ fontSize: '11px', fontWeight: 700, marginTop: '5px', color: item.growth >= 0 ? 'var(--success)' : 'var(--error)' }}>
                            {item.growth >= 0 ? '↑' : '↓'} {Math.abs(Math.round(item.growth))}% YoY
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {lastYearTotRev > 0 && (
                    <div style={{ padding: '10px 12px', borderRadius: 'var(--radius-md)', background: 'var(--bg-muted)', marginBottom: '10px' }}>
                      <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '6px' }}>{selectedYear - 1} comparison</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '12px' }}>
                        <div><div style={{ color: 'var(--text-muted)', marginBottom: '2px' }}>{t('goals.revenue')}</div><div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{fmt(lastYearTotRev)}</div></div>
                        <div><div style={{ color: 'var(--text-muted)', marginBottom: '2px' }}>{t('goals.orders')}</div><div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{lastYearTotOrd}</div></div>
                      </div>
                      {yoyRevGrowth !== null && (
                        <div style={{ fontSize: '12px', fontWeight: 700, marginTop: '6px', color: yoyRevGrowth >= 0 ? 'var(--success)' : 'var(--error)' }}>
                          {yoyRevGrowth >= 0 ? '↑' : '↓'} Revenue {Math.abs(Math.round(yoyRevGrowth))}% {yoyRevGrowth >= 0 ? t('goals.revenue_higher') : t('goals.revenue_lower')} {selectedYear - 1}
                        </div>
                      )}
                    </div>
                  )}

                  {bestMonth && (
                    <div style={{ padding: '10px 12px', borderRadius: 'var(--radius-md)', background: 'var(--primary-light)', border: '1px solid var(--primary-border)' }}>
                      <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--primary)', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <StatusIcon icon="trophy" size={11} color="var(--primary)" /> {t('goals.best_month')} {selectedYear}
                      </div>
                      <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>{MONTHS_SHORT[Number(bestMonth[0]) - 1]} — {fmt(bestMonth[1].revenue)}</div>
                      {bestMonthLastYear && Number(bestMonth[1].revenue) > 0 && Number(bestMonthLastYear[1].revenue) > 0 && (
                        <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>{t('goals.vs_last_year_best')} {fmt(bestMonthLastYear[1].revenue)}</div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* ── Heatmap ── */}
              <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', padding: '16px', marginBottom: '14px', boxShadow: 'var(--shadow-sm)' }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '4px' }}>{t('goals.heatmap_title')}</div>
                <div style={{ fontSize: '11.5px', color: 'var(--text-muted)', marginBottom: '12px' }}>{t('goals.heatmap_sub')} · Click a month to jump to it</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: '6px' }}>
                  {MONTHS_SHORT.map((m, i) => {
                    const mn  = i + 1
                    const rev = actuals[mn]?.revenue || 0
                    const gl  = Number(goals[mn]?.revenue_goal) || 0
                    const pct = gl > 0 ? (rev / gl) * 100 : null
                    const isFuture = selectedYear > currentYear || (selectedYear === currentYear && mn > currentMonth)
                    return <HeatCell key={mn} pct={isFuture ? null : pct} label={m} isCurrent={selectedYear === currentYear && mn === currentMonth} onClick={() => { setActiveTab('monthly'); setExpandedMonth(mn) }} />
                  })}
                </div>
                <div style={{ display: 'flex', gap: '12px', marginTop: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                  {[['#16A34A','100%+'], ['#4ADE80','80–99%'], ['#FCD34D','50–79%'], ['#F97316','20–49%'], ['#EF4444','<20%'], ['var(--bg-muted)', t('goals.heatmap_no_goal')]].map(([c, l]) => (
                    <div key={l} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10.5px', color: 'var(--text-muted)' }}>
                      <div style={{ width: '10px', height: '10px', borderRadius: 'var(--radius-sm)', background: c, border: '1px solid var(--border)' }} />{l}
                    </div>
                  ))}
                  <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10.5px', color: 'var(--primary)', fontWeight: 600 }}>
                    <div style={{ width: '10px', height: '10px', borderRadius: 'var(--radius-sm)', border: '2px solid var(--primary)' }} /> Current month
                  </div>
                </div>
              </div>

              {/* ── Achievement stats ── */}
              {achieveRate !== null && (
                <div className="goal-stat-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '14px' }}>
                  {[
                    { label: t('goals.months_with_goals'), value: monthsWithGoal.length, color: 'var(--text-primary)', icon: 'target' },
                    { label: t('goals.goals_hit'),         value: monthsHit.length,       color: 'var(--success)',       icon: 'check' },
                    { label: t('goals.success_rate_label'),value: achieveRate + '%',       color: achieveRate >= 70 ? 'var(--success)' : achieveRate >= 50 ? 'var(--warning)' : 'var(--error)', icon: achieveRate >= 70 ? 'check' : achieveRate >= 50 ? 'almost' : 'behind' },
                  ].map((s, i) => (
                    <div key={i} className="pc-card-hover" style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', padding: '16px', textAlign: 'center', boxShadow: 'var(--shadow-sm)' }}>
                      <StatusIcon icon={s.icon} size={20} color={s.color} />
                      <div style={{ fontSize: '24px', fontWeight: 800, color: s.color, marginTop: '8px' }}>{s.value}</div>
                      <div style={{ fontSize: '11.5px', color: 'var(--text-muted)', marginTop: '4px' }}>{s.label}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ══════════════════════════════════════════════
              MONTHLY TAB
          ══════════════════════════════════════════════ */}
          {activeTab === 'monthly' && (
            <div className="pc-fade-up">

              {/* Monthly bar chart */}
              <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', padding: '16px', marginBottom: '14px', boxShadow: 'var(--shadow-sm)' }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '2px' }}>{t('goals.monthly_glance_title')}</div>
                <div style={{ fontSize: '11.5px', color: 'var(--text-muted)', marginBottom: '14px' }}>{t('goals.monthly_glance_sub')}</div>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px', height: '120px', marginBottom: '4px' }}>
                  {MONTHS_SHORT.map((m, i) => {
                    const mn    = i + 1
                    const rev   = actuals[mn]?.revenue || 0
                    const gl    = Number(goals[mn]?.revenue_goal) || 0
                    const maxRev = Math.max(...Object.values(actuals).map(a => a.revenue), 1)
                    const barH  = Math.max(2, (rev / maxRev) * 100)
                    const status = getStatus(rev, gl, t)
                    const isFut  = selectedYear > currentYear || (selectedYear === currentYear && mn > currentMonth)
                    const isCurr = selectedYear === currentYear && mn === currentMonth
                    return (
                      <div key={mn} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', cursor: 'pointer' }}
                        onClick={() => setExpandedMonth(expandedMonth === mn ? null : mn)}>
                        <div style={{ width: '100%', borderRadius: 'var(--radius-sm)', height: barH + '%', background: isFut ? 'var(--border)' : isCurr ? 'var(--primary)' : status?.color || 'var(--border)', opacity: isFut ? 0.4 : 1, transition: 'height 0.3s', position: 'relative' }}>
                          {isCurr && <div style={{ position: 'absolute', top: '-5px', left: '50%', transform: 'translateX(-50%)', width: '6px', height: '6px', borderRadius: '50%', background: 'var(--primary)' }} />}
                        </div>
                        <div style={{ fontSize: '8px', color: isCurr ? 'var(--primary)' : 'var(--text-muted)', fontWeight: isCurr ? 700 : 400 }}>{m}</div>
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Month accordion rows */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {MONTHS.map((month, i) => {
                  const m         = i + 1
                  const goal      = goals[m] || { revenue_goal: '', order_goal: '' }
                  const actual    = actuals[m] || { revenue: 0, orders: 0 }
                  const lastAct   = lastYearActuals[m] || { revenue: 0, orders: 0 }
                  const revGoal   = Number(goal.revenue_goal) || 0
                  const ordGoal   = Number(goal.order_goal) || 0
                  const revStatus = getStatus(actual.revenue, revGoal, t)
                  const isFuture  = selectedYear > currentYear || (selectedYear === currentYear && m > currentMonth)
                  const isCurrent = selectedYear === currentYear && m === currentMonth
                  const isExpanded = expandedMonth === m

                  return (
                    <div key={m} style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', border: '1px solid ' + (isCurrent ? 'var(--primary)' : 'var(--border)'), overflow: 'hidden', boxShadow: 'var(--shadow-sm)' }}>
                      {/* Row header */}
                      <div onClick={() => setExpandedMonth(isExpanded ? null : m)}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', cursor: 'pointer', background: isCurrent ? 'var(--primary-light)' : 'var(--bg-card)', transition: 'background 0.12s' }}
                        onMouseEnter={e => { if (!isCurrent) e.currentTarget.style.background = 'var(--bg-muted)' }}
                        onMouseLeave={e => { e.currentTarget.style.background = isCurrent ? 'var(--primary-light)' : 'var(--bg-card)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: isFuture ? 'var(--border)' : revStatus?.color || 'var(--border)', flexShrink: 0 }} />
                          <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>{MONTHS_SHORT[i]}</span>
                          {isCurrent && <span style={{ fontSize: '10.5px', fontWeight: 700, padding: '2px 8px', borderRadius: 'var(--radius-full)', background: 'var(--primary)', color: '#fff' }}>{t('goals.now_badge')}</span>}
                          {!isFuture && revStatus && revGoal > 0 && !isCurrent && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: 600, color: revStatus.color }}>
                              <StatusIcon icon={revStatus.icon} size={11} color={revStatus.color} /> {revStatus.label}
                            </span>
                          )}
                          {isFuture && <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{t('goals.upcoming')}</span>}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>{fmt(actual.revenue)}</div>
                            <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{actual.orders} {t('goals.orders').toLowerCase()}</div>
                          </div>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2.5" strokeLinecap="round" style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s', flexShrink: 0 }}>
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </div>
                      </div>

                      {isExpanded && (
                        <div style={{ padding: '14px 16px', borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: '12px' }}>

                          {/* Progress bars */}
                          {revGoal > 0 && !isFuture && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '5px' }}>
                                  <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>{t('goals.revenue')}</span>
                                  <span style={{ color: revStatus?.color || 'var(--text-muted)', fontWeight: 700 }}>{fmt(actual.revenue)} / {fmt(revGoal)} ({Math.round(Math.min(100, (actual.revenue / revGoal) * 100))}%)</span>
                                </div>
                                <div style={{ height: '8px', borderRadius: 'var(--radius-full)', background: 'var(--border)' }}>
                                  <div style={{ height: '100%', borderRadius: 'var(--radius-full)', width: Math.min(100, (actual.revenue / revGoal) * 100) + '%', background: revStatus?.color || 'var(--primary)', transition: 'width 0.4s' }} />
                                </div>
                              </div>
                              {ordGoal > 0 && (
                                <div>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '5px' }}>
                                    <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>{t('goals.orders')}</span>
                                    <span style={{ color: 'var(--text-primary)', fontWeight: 700 }}>{actual.orders} / {ordGoal} ({Math.round(Math.min(100, (actual.orders / ordGoal) * 100))}%)</span>
                                  </div>
                                  <div style={{ height: '8px', borderRadius: 'var(--radius-full)', background: 'var(--border)' }}>
                                    <div style={{ height: '100%', borderRadius: 'var(--radius-full)', width: Math.min(100, (actual.orders / ordGoal) * 100) + '%', background: '#EC4899', transition: 'width 0.4s' }} />
                                  </div>
                                </div>
                              )}
                              {/* Motivation message */}
                              {revStatus && (
                                <div style={{ fontSize: '12px', color: revStatus.color, fontStyle: 'italic', fontWeight: 500 }}>
                                  {getMotivation(revStatus.pct, isCurrent, t)}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Last year comparison */}
                          {lastAct.revenue > 0 && (
                            <div style={{ padding: '10px 12px', borderRadius: 'var(--radius-md)', background: 'var(--bg-muted)' }}>
                              <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '6px' }}>{t('goals.vs_last_year')} {selectedYear - 1}</div>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '12px' }}>
                                {[[t('goals.revenue'), fmt(lastAct.revenue), actual.revenue, lastAct.revenue], [t('goals.orders'), lastAct.orders, actual.orders, lastAct.orders]].map(([l, v, curr, prev]) => (
                                  <div key={l}>
                                    <div style={{ color: 'var(--text-muted)', marginBottom: '2px' }}>{l} {selectedYear - 1}</div>
                                    <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{v}</div>
                                    {prev > 0 && <div style={{ fontSize: '11px', fontWeight: 700, color: curr >= prev ? 'var(--success)' : 'var(--error)' }}>{curr >= prev ? '+' : ''}{Math.round(((curr - prev) / prev) * 100)}%</div>}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Quick-set buttons — SURFACED prominently above inputs */}
                          {lastAct.revenue > 0 && (
                            <div>
                              <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '6px' }}>Quick set from last year</div>
                              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                {[0, 10, 20, 30].map(pct => (
                                  <button key={pct} onClick={() => setGoalFromLastYear(m, pct)}
                                    style={{ padding: '5px 12px', borderRadius: 'var(--radius-full)', border: '1px solid var(--border)', background: 'var(--bg-card)', fontSize: '11.5px', fontWeight: 600, color: 'var(--text-primary)', cursor: 'pointer', transition: 'all 0.12s' }}
                                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--primary)'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderColor = 'var(--primary)' }}
                                    onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg-card)'; e.currentTarget.style.color = 'var(--text-primary)'; e.currentTarget.style.borderColor = 'var(--border)' }}>
                                    {pct === 0 ? 'Same as last year' : '+' + pct + '%'}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Goal inputs */}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                            <div>
                              <label style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>{t('goals.revenue_goal_label')} ({sym()})</label>
                              <input type="number" value={goal.revenue_goal}
                                onChange={e => setGoals(g => ({ ...g, [m]: { ...g[m], revenue_goal: e.target.value } }))}
                                placeholder={t('goals.revenue_goal_ph')} style={{ ...inp }}
                                onFocus={e => e.target.style.borderColor = 'var(--primary)'}
                                onBlur={e => e.target.style.borderColor = 'var(--border)'} />
                            </div>
                            <div>
                              <label style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>{t('goals.order_goal_label')}</label>
                              <input type="number" value={goal.order_goal}
                                onChange={e => setGoals(g => ({ ...g, [m]: { ...g[m], order_goal: e.target.value } }))}
                                placeholder={t('goals.order_goal_ph')} style={{ ...inp }}
                                onFocus={e => e.target.style.borderColor = 'var(--primary)'}
                                onBlur={e => e.target.style.borderColor = 'var(--border)'} />
                            </div>
                          </div>

                          {/* Personal note */}
                          <div>
                            <label style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>{t('goals.personal_note')}</label>
                            <textarea value={notes[m] || ''} onChange={e => setNotes(n => ({ ...n, [m]: e.target.value }))}
                              placeholder={t('goals.note_ph')} rows={2} style={{ ...inp, resize: 'none' }}
                              onFocus={e => e.target.style.borderColor = 'var(--primary)'}
                              onBlur={e => e.target.style.borderColor = 'var(--border)'} />
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>

              <button onClick={handleSave} disabled={saving}
                style={{ width: '100%', padding: '13px', borderRadius: 'var(--radius-lg)', background: 'var(--primary)', border: 'none', fontSize: '13px', fontWeight: 700, color: '#fff', cursor: 'pointer', opacity: saving ? 0.6 : 1, marginTop: '14px', marginBottom: '20px' }}>
                {saving ? t('goals.saving_btn') : t('goals.save_btn')}
              </button>
            </div>
          )}

          {/* ══════════════════════════════════════════════
              QUARTERLY TAB
          ══════════════════════════════════════════════ */}
          {activeTab === 'quarterly' && (
            <div className="pc-fade-up">
              <div className="goal-stat-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '14px' }}>
                {quarters.map((q) => {
                  const status = getStatus(q.actual, q.goal, t)
                  return (
                    <div key={q.label} className="pc-card-hover" style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', border: '1px solid ' + (status?.color ? status.color + '40' : 'var(--border)'), padding: '16px', boxShadow: 'var(--shadow-sm)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                        <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)' }}>{q.label}</div>
                        {status && (
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: 'var(--radius-full)', background: status.bg, color: status.color }}>
                            <StatusIcon icon={status.icon} size={10} color={status.color} /> {status.label}
                          </div>
                        )}
                      </div>
                      <div style={{ fontSize: '22px', fontWeight: 800, color: 'var(--text-primary)', marginBottom: '3px' }}>{fmt(q.actual)}</div>
                      {q.goal > 0 && (
                        <>
                          <div style={{ fontSize: '11.5px', color: 'var(--text-muted)', marginBottom: '8px' }}>{t('goals.of')} {fmt(q.goal)} {t('goals.of_goal')}</div>
                          <div style={{ height: '6px', borderRadius: 'var(--radius-full)', background: 'var(--border)', marginBottom: '8px' }}>
                            <div style={{ height: '100%', borderRadius: 'var(--radius-full)', width: Math.min(100, (q.actual / q.goal) * 100) + '%', background: status?.color || 'var(--primary)', transition: 'width 0.4s' }} />
                          </div>
                        </>
                      )}
                      <div style={{ fontSize: '11.5px', color: 'var(--text-muted)' }}>{q.orders} {t('goals.orders').toLowerCase()}</div>
                      <div style={{ fontSize: '11.5px', color: 'var(--text-muted)' }}>{q.months.map(mn => MONTHS_SHORT[mn - 1]).join(' · ')}</div>
                    </div>
                  )
                })}
              </div>

              <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', padding: '16px', marginBottom: '14px', boxShadow: 'var(--shadow-sm)' }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '12px' }}>{t('goals.quarterly_insights')}</div>
                {(() => {
                  const best  = quarters.reduce((a, b) => a.actual > b.actual ? a : b)
                  const worst = quarters.filter(q => q.actual > 0).reduce((a, b) => a.actual < b.actual ? a : b, quarters[0])
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {best.actual > 0 && (
                        <div style={{ padding: '10px 12px', borderRadius: 'var(--radius-md)', background: '#F0FDF4', border: '1px solid #BBF7D0', fontSize: '12.5px', color: 'var(--success)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <StatusIcon icon="trophy" size={14} color="var(--success)" />
                          {t('goals.quarterly_best')} <strong>{best.label}</strong> — {fmt(best.actual)} {t('goals.quarterly_revenue')} · {best.orders} {t('goals.orders').toLowerCase()}
                        </div>
                      )}
                      {worst && worst.actual > 0 && worst.label !== best.label && (
                        <div style={{ padding: '10px 12px', borderRadius: 'var(--radius-md)', background: '#FEF2F2', border: '1px solid #FECACA', fontSize: '12.5px', color: 'var(--error)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <StatusIcon icon="behind" size={14} color="var(--error)" />
                          {t('goals.quarterly_lowest')} <strong>{worst.label}</strong> — {fmt(worst.actual)} {t('goals.quarterly_revenue')} · {worst.orders} {t('goals.orders').toLowerCase()}
                        </div>
                      )}
                      {quarters.filter(q => q.goal > 0 && q.actual >= q.goal).length > 0 && (
                        <div style={{ padding: '10px 12px', borderRadius: 'var(--radius-md)', background: 'var(--primary-light)', border: '1px solid var(--primary-border)', fontSize: '12.5px', color: 'var(--primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <StatusIcon icon="check" size={14} color="var(--primary)" />
                          {t('goals.quarterly_met')} <strong>{quarters.filter(q => q.goal > 0 && q.actual >= q.goal).map(q => q.label).join(', ')}</strong>
                        </div>
                      )}
                    </div>
                  )
                })()}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button onClick={handleSave} disabled={saving}
                  style={{ padding: '11px 24px', borderRadius: 'var(--radius-lg)', background: 'var(--primary)', border: 'none', fontSize: '13px', fontWeight: 700, color: '#fff', cursor: 'pointer', opacity: saving ? 0.6 : 1, marginBottom: '20px' }}>
                  {saving ? t('goals.saving_btn') : t('goals.save_btn')}
                </button>
              </div>
            </div>
          )}

          </>
        )}
      </div>

      <style>{`
        @keyframes goal-spin { to { transform: rotate(360deg) } }
        @keyframes pc-fade-up { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        .pc-fade-up { animation: pc-fade-up 0.4s ease-out both; }
        .pc-card-hover { transition: transform 0.15s ease, box-shadow 0.15s ease; }
        .pc-card-hover:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(11,31,58,0.08) !important; }
        .goal-grid      { display: grid !important; }
        .goal-stat-grid { display: grid !important; }
        @media(max-width:900px) {
          .goal-grid      { grid-template-columns: 1fr !important; }
          .goal-stat-grid { grid-template-columns: 1fr 1fr !important; }
        }
        @media(max-width:600px) {
          .goal-stat-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  )
}

export default Goals